import {
  Box,
  Button,
  Group,
  List,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconChevronLeft, IconFoldUp } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { createBlacklist, importBlacklist } from "../../../../api/BlacklistAPI";
import CreatableBlacklistSelector from "../../../../components/Selectors/CreatableBlacklistSelector";
import { useConfigStore } from "../../../../stores/ConfigStore";
import { useRouteLoaderData } from "react-router-dom";
import ContactsResponse from "../../lists/components/jobs/arguments/ContactsResponse";

const BlacklistImportStep = ({
  data,
  handlePrev,
  fields,
  setImporting,
  onFinish,
  files,
}) => {
  const blacklistProfile = useRouteLoaderData("blacklist");
  const lang = useLanguage((s) => s.language);
  const [importStats, setImportStats] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState(
    files?.[0]?.name?.substring(0, files?.[0]?.name.lastIndexOf(".")) || ""
  );
  const [list, setList] = useState(blacklistProfile);
  const [listState, setListState] = useState(blacklistProfile ? 2 : 1);

  const finalData = useMemo(() => {
    const datafields = useConfigStore.getState().datafields;
    const parsedFields = {};
    Object.keys(data[0]).forEach((key) => {
      parsedFields[key] = datafields.find(
        (x) => x.identifier_placeholder === fields[key]
      )?.id;
    });

    let contacts = [];
    data.forEach((contact) => {
      Object.keys(contact).forEach((column) => {
        if (parsedFields[column] && contact[column]) {
          contacts.push({
            data_field_id: parsedFields[column],
            value: contact[column]?.toString(),
          });
        }
      });
    });

    return contacts;
  }, [data, fields]);

  const handleImport = useCallback(
    async (e) => {
      e.preventDefault();
      let contains_match = e.target.contains_match.value === "true" ? true : false;
      setImporting(true);

      async function mainFunction(id) {
        return importBlacklist(id, finalData, true)
          .then((response) => {
            setImportStats(response.data);
          })
          .catch((err) => {
            let err_data = err.response.data;
            setError(err_data);
          })
          .finally(() => {
            setImporting(false);
          });
      }

      if (!list?.id) {
        return createBlacklist({ name, contains_match })
          .catch((createError) => {
            let state_message = createError.response.data.state_message;
            // if (state_message === "empty_name") {
            //   return handleErrors(state_message);
            // }
          })
          .then((response) => {
            setList({ id: response.data.blacklist_id });
            mainFunction(response.data.blacklist_id);
          });
      }
      return mainFunction(list?.id);
    },
    [finalData, list?.id, name, setImporting]
  );

  const main = useMemo(() => {
    if (importStats) {
      return (
        <>
          <Box w={200} mx={"auto"}>
            <ContactsResponse
              item={{ import_contacts_response: importStats }}
            />
          </Box>

          <Button
            variant="outline"
            onClick={() => onFinish(list.id)}
            w={300}
            size="md"
            mx={"auto"}
          >
            {lang.global.finish}
          </Button>
        </>
      );
    }
    if (error) {
      if (error?.state_message === "import_limit_exceeded") {
        return (
          <Text>
            {
              lang.unsubscribers.import.step_importing.error
                .import_limit_exceeded
            }{" "}
            ({data.length}{" "}
            {lang.unsubscribers.import.step_importing.error.rows_to_import})
          </Text>
        );
      }

      if (error?.errors?.length > 0) {
        return error.errors.map((err, index) =>
          typeof err === "string" ? (
            <Text key={index} c={"red"} ta={"center"} fw={500}>
              {" "}
              {err}{" "}
            </Text>
          ) : (
            <Text key={index} c={"red"} ta={"center"} fw={500}>
              {lang.unsubscribers.import.step_importing.error.error_on_line}{" "}
              {err?.line} :{" "}
              {err?.error?.includes("format")
                ? lang.unsubscribers.import.step_importing.error.unknown_format
                : err?.error}
            </Text>
          )
        );
      }
    } else {
      return (
        <form onSubmit={handleImport}>
          <Text>
            <b>{finalData?.length}</b> données seront importées. <br /> <br />
          </Text>

          <Group wrap="nowrap" align="end" gap={"xl"}>
            <Stack w="100%" gap={4}>
              <Radio
                label={lang.targeting_linkedin_google.create_new_list}
                name="list_state"
                checked={listState === 1}
                size="md"
                onChange={(e) => setListState(1)}
              />
              <TextInput
                id="new-list-name"
                name="blacklist_name"
                value={name}
                w={"100%"}
                disabled={listState !== 1}
                styles={{ label: { fontSize: 13 } }}
                label={lang.targeting_linkedin_google.blacklist_name}
                placeholder={lang.targeting_linkedin_google.blacklist_name}
                onChange={(e) => setName(e.target.value)}
                required={listState === 1}
                size="md"
              />
            </Stack>
            <Stack w="100%" gap={4}>
              <Radio
                label={lang.targeting_linkedin_google.blacklist_import}
                name="list_state"
                size="md"
                checked={listState === 2}
                onChange={(e) => setListState(2)}
              />
              <CreatableBlacklistSelector
                value={list}
                setValue={setList}
                label={lang.targeting_linkedin_google.select_blacklist}
                placeholder={lang.contact_lists.create.dialog.desc}
                w={"100%"}
                disabled={listState !== 2}
                styles={{ label: { fontSize: 13 } }}
                required={listState === 2}
                disableCreate
                description={" "}
                size="md"
              />
            </Stack>
          </Group>

          <Stack>
            <Select
              label="Type d'exclusion"
              data={
                [
                  { label: "Exact (exclusion des mots clés au sens stricte du terme)", value: "false" },
                  { label: "Contient (exclusion de tous les contacts qui contiennent les mots clés à exclure)", value: "true" },
                ]
              }
              defaultValue={blacklistProfile?.contains_match?.toString() || "false"}
              name="contains_match"
              size="md"
              mt={"md"}
            />
          </Stack>

          <Group justify="center" mt={"lg"}>
            <Button
              disabled={listState === 1 ? !name : !list}
              mt={"md"}
              variant="filled"
              leftSection={<IconFoldUp />}
              type="submit"
              size="md"
              w={300}
            >
              {lang.contact_lists.import.step_importing.import}
            </Button>
          </Group>
        </form>
      );
    }
  }, [blacklistProfile?.contains_match, data.length, error, finalData?.length, handleImport, importStats, lang.contact_lists.create.dialog.desc, lang.contact_lists.import.step_importing.import, lang.global.finish, lang.targeting_linkedin_google.blacklist_import, lang.targeting_linkedin_google.blacklist_name, lang.targeting_linkedin_google.create_new_list, lang.targeting_linkedin_google.select_blacklist, lang.unsubscribers.import.step_importing.error.error_on_line, lang.unsubscribers.import.step_importing.error.import_limit_exceeded, lang.unsubscribers.import.step_importing.error.rows_to_import, lang.unsubscribers.import.step_importing.error.unknown_format, list, listState, name, onFinish]);

  return (
    <>
      {main}
      {!importStats && (
        <Group justify="center">
          <Button
            variant="subtle"
            onClick={handlePrev}
            leftSection={<IconChevronLeft />}
            size="md"
            w={300}
          >
            {lang.global.back}
          </Button>
        </Group>
      )}
    </>
  );
};

export default BlacklistImportStep;
